import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * EAA-1821: "does this row of controls still fit, and if not, collapse it".
 *
 * A toolbar that wraps to a second line is not free: on a fit-to-shell page
 * every wrapped row is a row of table that no longer fits, and the controls
 * that wrapped are the ones an operator reaches for while reading the table.
 * Collapsing them behind one trigger keeps the toolbar one line tall at any
 * width.
 *
 * WHY THE MEASUREMENT IS CACHED. The naive version — collapse when
 * `scrollWidth > clientWidth` — oscillates: collapsing removes the very content
 * whose width caused it, the row then fits, it expands, and it overflows again.
 * The usual fix is to render the content twice and measure an off-screen twin;
 * that was tried and rejected here, because a duplicate of a toolbar is a
 * duplicate of every control in it — doubled in the accessibility tree, doubled
 * for anything that queries the DOM, and quietly wrong the moment someone
 * forgets `inert`.
 *
 * Instead the natural width is measured from the REAL content whenever it is
 * expanded, and remembered. While collapsed there is nothing to measure, so the
 * remembered width decides when to expand again — which is exactly the value
 * that would have been measured anyway. No duplicate DOM, no feedback loop.
 *
 * The one imprecision: if the content's natural width changes WHILE collapsed
 * (a filter chip growing because a value was picked inside the collapsed menu),
 * the remembered width is stale until the next expansion. That only shifts the
 * threshold by a few pixels and self-corrects on the first expand.
 *
 * The container is the BUDGET, not the whole toolbar: give the collapsible slot
 * `flex: 1 1 auto; min-width: 0` and attach `containerRef` to it. Everything
 * else on the toolbar should keep a width that does NOT depend on whether the
 * content is collapsed — a sibling that grows to fill the freed space changes
 * the budget as a *result* of the decision, which is how a stable measurement
 * turns back into a flip-flop.
 *
 * ```tsx
 * const { containerRef, contentRef, collapsed } = useOverflowCollapse();
 *
 * <div ref={containerRef} style={{ flex: '1 1 auto', minWidth: 0 }}>
 *     {collapsed ? <MoreButton /> : <div ref={contentRef}>{content}</div>}
 * </div>
 * ```
 */

export interface UseOverflowCollapseOptions {
    /**
     * Px of slack required before expanding again. Without it, a width that
     * lands exactly on the threshold flickers between the two states as
     * sub-pixel layout rounding moves it back and forth.
     */
    tolerance?: number;
    /** Escape hatch: force the expanded state and skip measuring entirely. */
    disabled?: boolean;
}

export interface UseOverflowCollapseResult {
    /** The slot whose width is the budget. Needs `min-width: 0` to shrink at all. */
    containerRef: (node: HTMLDivElement | null) => void;
    /**
     * The full content, mounted only while expanded. Must not shrink — give it
     * `flex: none` and `white-space: nowrap` so its `scrollWidth` is its natural
     * width even in the frame where it no longer fits.
     */
    contentRef: (node: HTMLDivElement | null) => void;
    /** True when the full content does not fit the container. */
    collapsed: boolean;
}

const DEFAULT_TOLERANCE = 8;

export function useOverflowCollapse({
    tolerance = DEFAULT_TOLERANCE,
    disabled = false,
}: UseOverflowCollapseOptions = {}): UseOverflowCollapseResult {
    const containerNodeRef = useRef<HTMLDivElement | null>(null);
    const contentNodeRef = useRef<HTMLDivElement | null>(null);
    /** Natural width of the content, remembered from the last time it was expanded. */
    const naturalWidthRef = useRef(0);
    const observerRef = useRef<ResizeObserver | null>(null);
    const [collapsed, setCollapsed] = useState(false);

    const measure = useCallback(() => {
        const container = containerNodeRef.current;
        if (!container || disabled) return;

        const available = container.clientWidth;
        // A container that has not been laid out yet reports 0 — collapsing on
        // that would flash the collapsed state on every mount.
        if (available === 0) return;

        const content = contentNodeRef.current;
        if (content) {
            const natural = content.scrollWidth;
            // Guard the same 0 case: a node mid-unmount measures 0, and taking
            // that would say "nothing needs space" and expand forever.
            if (natural > 0) naturalWidthRef.current = natural;
        }
        const needed = naturalWidthRef.current;
        if (needed === 0) return;

        setCollapsed((wasCollapsed) =>
            wasCollapsed ? needed > available - tolerance : needed > available
        );
    }, [tolerance, disabled]);

    /**
     * EAA-1821: ONE observer, created once and never torn down on a state
     * change. An earlier version rebuilt it in an effect that depended on
     * `collapsed`; because `ResizeObserver` fires immediately on `observe()`,
     * every flip re-measured, which could flip it back — React caught it as
     * "Maximum update depth exceeded". The observer's lifetime must not depend
     * on the value it produces.
     */
    const ensureObserver = useCallback((): ResizeObserver => {
        if (!observerRef.current) observerRef.current = new ResizeObserver(measure);
        return observerRef.current;
    }, [measure]);

    const containerRef = useCallback(
        (node: HTMLDivElement | null) => {
            const observer = ensureObserver();
            if (containerNodeRef.current) observer.unobserve(containerNodeRef.current);
            containerNodeRef.current = node;
            if (node) {
                observer.observe(node);
                measure();
            }
        },
        [ensureObserver, measure]
    );

    // Callback ref rather than an effect: the content element mounts and
    // unmounts as `collapsed` flips, and this is the one hook that must react to
    // that WITHOUT taking `collapsed` as a dependency.
    const contentRef = useCallback(
        (node: HTMLDivElement | null) => {
            const observer = ensureObserver();
            if (contentNodeRef.current) observer.unobserve(contentNodeRef.current);
            contentNodeRef.current = node;
            if (node) {
                observer.observe(node);
                measure();
            }
        },
        [ensureObserver, measure]
    );

    useEffect(() => {
        if (disabled) setCollapsed(false);
    }, [disabled]);

    /**
     * EAA-1821: re-observes on SETUP, not just disconnecting on teardown.
     *
     * The obvious version — `useEffect(() => () => observer.disconnect(), [])` —
     * is broken under StrictMode, which mounts, tears down and re-runs effects
     * without re-running ref callbacks. The teardown disconnected the observer
     * and nothing ever observed again, so the toolbar measured correctly once at
     * mount and then never responded to a resize. It looked like it worked,
     * because navigating to the page at a narrow width still collapsed
     * correctly — only DRAGGING the window did nothing.
     *
     * `observe()` on an already-observed element replaces the observation rather
     * than stacking, so doing it here as well as in the ref callbacks is safe.
     */
    useEffect(() => {
        const observer = ensureObserver();
        if (containerNodeRef.current) observer.observe(containerNodeRef.current);
        if (contentNodeRef.current) observer.observe(contentNodeRef.current);
        return () => observer.disconnect();
    }, [ensureObserver]);

    return { containerRef, contentRef, collapsed: disabled ? false : collapsed };
}
